import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.array.find-index.js";
import "core-js/modules/es.array.from.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.set.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { useRef, useEffect } from 'react';
import { KEY } from 'utils/ui/constants';
import useEventListener from 'hooks/useEventListener';
export var NONE_SELECTED = 'none';
export var SOME_SELECTED = 'some';
export var ALL_SELECTED = 'all';
function getSelectAllState(rowIds, selectedItemIds) {
  if (selectedItemIds.length === 0) {
    return NONE_SELECTED;
  }
  var itemIsSelected = function itemIsSelected(itemId) {
    return selectedItemIds.includes(itemId);
  };
  var areAllSelected = rowIds.every(itemIsSelected);
  if (areAllSelected) {
    return ALL_SELECTED;
  }
  var areSomeSelected = rowIds.some(itemIsSelected);
  return areSomeSelected ? SOME_SELECTED : NONE_SELECTED;
}
function useRowSelection(_ref) {
  var rowIds = _ref.rowIds,
    highlightedRowIndex = _ref.highlightedRowIndex,
    selectedItemIds = _ref.selectedItemIds,
    _ref$allowTogglingSin = _ref.allowTogglingSingleSelection,
    allowTogglingSingleSelection = _ref$allowTogglingSin === void 0 ? false : _ref$allowTogglingSin,
    onChange = _ref.onChange;
  var metaKeyPressedRef = useRef(false);
  var shiftKeyPressedRef = useRef(false);
  function captureModifierKeys(event) {
    metaKeyPressedRef.current = event.metaKey || event.ctrlKey;
    shiftKeyPressedRef.current = event.shiftKey;
  }
  function resetModifierKeys() {
    metaKeyPressedRef.current = false;
    shiftKeyPressedRef.current = false;
  }
  useEventListener('keyup', captureModifierKeys);
  useEventListener('keydown', captureModifierKeys);
  // Reset modifier keys when the browser tab/window loses focus
  useEventListener('blur', resetModifierKeys, {
    targetElement: window
  });
  useEventListener('selectstart', function (event) {
    // Prevent range selection on Shift + Click
    if (shiftKeyPressedRef.current) {
      event.preventDefault();
    }
  });

  // Track the last item that was single-selected
  // for range selection
  var lastSelectedItem = useRef(undefined);
  useEffect(function () {
    // If there's no last selected item, initialise it
    // with the topmost selected item
    if (!lastSelectedItem.current && selectedItemIds.length) {
      var topMostSelectedItem = rowIds.find(function (itemId) {
        return selectedItemIds.includes(itemId);
      });
      lastSelectedItem.current = topMostSelectedItem;
    }
  }, [rowIds, selectedItemIds]);
  function trackLastSelectedRow(itemId) {
    lastSelectedItem.current = itemId;
  }
  var selectAllState = getSelectAllState(rowIds, selectedItemIds);
  function getIsRowSelected(itemId) {
    return selectAllState === ALL_SELECTED || selectedItemIds.includes(itemId);
  }
  function removeFromSelection(itemIds) {
    onChange(selectedItemIds.filter(function (selectedItem) {
      return !itemIds.includes(selectedItem);
    }));
  }
  function addToSelection(itemIds) {
    var uniqueSelection = Array.from(new Set([].concat(_toConsumableArray(selectedItemIds), _toConsumableArray(itemIds))));
    onChange(uniqueSelection);
  }
  function toggleSingleRow(itemId) {
    if (getIsRowSelected(itemId)) {
      removeFromSelection([itemId]);
    } else {
      addToSelection([itemId]);
    }
  }
  function handleRangeSelection(itemId) {
    var highlightedIndex = typeof highlightedRowIndex === 'number' ? highlightedRowIndex : highlightedRowIndex.current;
    if (shiftKeyPressedRef.current && lastSelectedItem.current !== itemId) {
      // Range selection when shift is pressed
      var lastClickedIndex = rowIds.findIndex(function (rowId) {
        return rowId === lastSelectedItem.current;
      });
      if (lastClickedIndex !== -1) {
        var range = rowIds.slice(Math.min(highlightedIndex, lastClickedIndex), Math.max(highlightedIndex, lastClickedIndex) + 1);
        addToSelection(range);
      }
      return true;
    } else if (metaKeyPressedRef.current) {
      // Toggle selection when cmd/ctrl is pressed
      toggleSingleRow(itemId);
      lastSelectedItem.current = itemId;
      return true;
    }
    return false;
  }
  function getSelectRowHandler(itemId) {
    return function () {
      if (!handleRangeSelection(itemId)) {
        toggleSingleRow(itemId);
        trackLastSelectedRow(itemId);
      }
    };
  }
  function getSelectSingleRowHandler(itemId) {
    return function () {
      if (!handleRangeSelection(itemId)) {
        if (allowTogglingSingleSelection && getIsRowSelected(itemId) && selectedItemIds.length === 1) {
          onChange([]);
        } else {
          // Deselect all & select single
          onChange([itemId]);
        }
        trackLastSelectedRow(itemId);
      }
    };
  }
  function toggleSelectAll() {
    if (selectAllState === NONE_SELECTED) {
      onChange(rowIds);
    } else {
      onChange([]);
    }
  }
  function handleGridSelectionKeys(event) {
    var highlightedIndex = typeof highlightedRowIndex === 'number' ? highlightedRowIndex : highlightedRowIndex.current;
    var highlightedRowId = rowIds[highlightedIndex];
    if (event.key === KEY.SPACE && event.shiftKey) {
      event.preventDefault();
      toggleSingleRow(highlightedRowId);
    }
    if (event.key === KEY.ARROW_DOWN && event.shiftKey) {
      event.preventDefault();
      var nextRowId = rowIds[highlightedIndex + 1];
      addToSelection([highlightedRowId, nextRowId]);
    }
    if (event.key === KEY.ARROW_UP && event.shiftKey) {
      event.preventDefault();
      addToSelection([highlightedRowId, rowIds[highlightedIndex - 1]]);
    }
    if (event.key === KEY.PAGE_DOWN && event.shiftKey) {
      event.preventDefault();
      var range = rowIds.slice(highlightedIndex);
      addToSelection(range);
    }
    if (event.key === KEY.PAGE_UP && event.shiftKey) {
      event.preventDefault();
      var _range = rowIds.slice(0, highlightedIndex + 1);
      addToSelection(_range);
    }
    if (event.key === 'a' && event.ctrlKey) {
      event.preventDefault();
      if (selectAllState === ALL_SELECTED) {
        onChange([]);
      } else {
        onChange(rowIds);
      }
    }
  }
  return {
    selectAllState: selectAllState,
    getIsRowSelected: getIsRowSelected,
    getSelectRowHandler: getSelectRowHandler,
    getSelectSingleRowHandler: getSelectSingleRowHandler,
    toggleSelectAll: toggleSelectAll,
    handleGridSelectionKeys: handleGridSelectionKeys
  };
}
export default useRowSelection;